'use client';

import React, {
  createContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

type ModalStackItem = {
  id: string;
  props?: Record<string, unknown>;
};

type ModalRenderFn = (props: Record<string, unknown>) => ReactNode;

type NestedModalContextType = {
  stack: ModalStackItem[];
  openModal: (modalId: string, props?: Record<string, unknown>) => void;
  /** Append several modals in one state update (correct z-order; avoids flushSync after async). */
  pushModalStack: (entries: ModalStackItem[]) => void;
  closeModal: (modalId: string) => void;
  closeTopModal: () => void;
  registerModal: (id: string, render: ModalRenderFn) => void;
};

const NestedModalContext = createContext<NestedModalContextType | undefined>(
  undefined
);

/** Above app chrome (z-50), promo banner (z-20), etc. */
const BASE_Z_INDEX = 12_000;
const Z_STEP = 100;

/** Use on `ToastContainer` `style` so toasts stay above the modal stack. */
export const TOAST_CONTAINER_Z_INDEX = BASE_Z_INDEX + 10 * Z_STEP;

export function NestedModalProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<ModalStackItem[]>([]);
  const [portalReady, setPortalReady] = useState(false);
  const registryRef = useRef<Record<string, ModalRenderFn>>({});
  /** Captured body overflow only when we first lock (avoid saving "hidden" when stack grows). */
  const bodyOverflowBeforeLockRef = useRef<string | null>(null);
  const hasLockedBodyScrollRef = useRef(false);

  useEffect(() => {
    setPortalReady(true);
  }, []);

  const registerModal = useCallback((id: string, render: ModalRenderFn) => {
    registryRef.current[id] = render;
  }, []);

  const openModal = useCallback(
    (modalId: string, props?: Record<string, unknown>) => {
      setStack((prev) => [...prev, { id: modalId, props: props ?? {} }]);
    },
    []
  );

  const pushModalStack = useCallback((entries: ModalStackItem[]) => {
    if (entries.length === 0) return;
    setStack((prev) => [
      ...prev,
      ...entries.map((e) => ({ id: e.id, props: e.props ?? {} })),
    ]);
  }, []);

  const closeModal = useCallback((modalId: string) => {
    setStack((prev) => prev.filter((item) => item.id !== modalId));
  }, []);

  const closeTopModal = useCallback(() => {
    setStack((prev) => (prev.length > 0 ? prev.slice(0, -1) : prev));
  }, []);

  useEffect(() => {
    if (stack.length > 0) {
      if (!hasLockedBodyScrollRef.current) {
        bodyOverflowBeforeLockRef.current = document.body.style.overflow;
        hasLockedBodyScrollRef.current = true;
      }
      document.body.style.overflow = 'hidden';
      return;
    }

    if (hasLockedBodyScrollRef.current) {
      document.body.style.overflow = bodyOverflowBeforeLockRef.current ?? '';
      bodyOverflowBeforeLockRef.current = null;
      hasLockedBodyScrollRef.current = false;
    }
  }, [stack.length]);

  useEffect(() => {
    return () => {
      if (hasLockedBodyScrollRef.current) {
        document.body.style.overflow = bodyOverflowBeforeLockRef.current ?? '';
        hasLockedBodyScrollRef.current = false;
        bodyOverflowBeforeLockRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && stack.length > 0) {
        closeTopModal();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [stack.length, closeTopModal]);

  const value: NestedModalContextType = {
    stack,
    openModal,
    pushModalStack,
    closeModal,
    closeTopModal,
    registerModal,
  };

  const layers =
    stack.length === 0
      ? null
      : stack.map((item, index) => {
          const render = registryRef.current[item.id];
          if (!render) return null;
          const zIndex = BASE_Z_INDEX + index * Z_STEP;
          return (
            <NestedModalLayer
              key={`${item.id}-${index}`}
              zIndex={zIndex}
              onBackdropClick={closeTopModal}
            >
              {render(item.props ?? {})}
            </NestedModalLayer>
          );
        });

  return (
    <NestedModalContext.Provider value={value}>
      {children}
      {portalReady && typeof document !== 'undefined'
        ? createPortal(<>{layers}</>, document.body)
        : null}
    </NestedModalContext.Provider>
  );
}

function NestedModalLayer({
  children,
  zIndex,
  onBackdropClick,
}: {
  children: ReactNode;
  zIndex: number;
  onBackdropClick: () => void;
}) {
  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex }}
      aria-modal
    >
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        style={{ zIndex: zIndex - 1 }}
        onClick={onBackdropClick}
        aria-hidden
      />
      <div
        className="relative w-full max-w-full overflow-visible"
        style={{ zIndex }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
